import * as tracking from '../util/tracking';
import debounce from '../util/debounce';

const hitsWithNoQuery = 3;
const hitsWithQuery = 3;
const BRAND_FIELD_NAME = 'brand_name';
const STORE_FIELD_NAME = 'store_name';

let resultCache;
let queryCache;

const debounced = debounce(items => Promise.resolve(items), 100);

export function createBrandStoreSearchPlugin({ searchClient }) {
  return {
    getSources({ query }) {
      return debounced([
        {
          sourceId: 'brands',
          async getItems() {
            // Check if this query is currently cached
            if (resultCache && query === queryCache) return resultCache;

            // Request data
            const result = await searchClient.multiSearch.perform({
              searches: [
                {
                  collection: window.cece.typesense.collectionName,
                  q: query || '*',
                  query_by: `${BRAND_FIELD_NAME}`,
                  facet_by: `${BRAND_FIELD_NAME}`,
                  facet_query: query
                    ? `${BRAND_FIELD_NAME}:${query}`
                    : undefined,
                  max_facet_values: query ? hitsWithQuery : hitsWithNoQuery,
                  filter_by: `visible:true && country_site_codes:${
                    window.cece.siteCountryCode
                  }${
                    window.cece.searchSection
                      ? ' && section_names:' + window.cece.searchSection
                      : ''
                  }`,
                  per_page: 0,
                },
                {
                  collection: window.cece.typesense.collectionName,
                  q: query || '*',
                  query_by: `${STORE_FIELD_NAME}`,
                  facet_by: `${STORE_FIELD_NAME}`,
                  facet_query: query
                    ? `${STORE_FIELD_NAME}:${query}`
                    : undefined,
                  max_facet_values: query ? hitsWithQuery : hitsWithNoQuery,
                  filter_by: `visible:true && country_site_codes:${
                    window.cece.siteCountryCode
                  }${
                    window.cece.searchSection
                      ? ' && section_names:' + window.cece.searchSection
                      : ''
                  }`,
                  per_page: 0,
                },
              ],
            });

            let brands = result.results[0].facet_counts?.[0]?.counts || [];
            let stores = result.results[1].facet_counts?.[0]?.counts || [];

            // Take max two brands if there is also a store hit
            if (brands.length > hitsWithQuery - 2 && stores.length > 0) {
              brands = brands.slice(0, 2);
            }

            // Fill up the hits to `hitsWithQuery` with store hits
            if (brands.length < hitsWithQuery) {
              stores = stores.slice(0, hitsWithQuery - brands.length);
            }

            // Mark if results are brand or store hits
            brands.forEach(b => {
              b.brand = true;
              return b;
            });
            stores.forEach(s => {
              s.store = true;
              return s;
            });

            // Cache and return
            queryCache = query;
            resultCache = [...brands, ...stores];
            return resultCache;
          },
          templates: {
            header() {
              return window.i18n.header.searchBrandsAndStoresHeader;
            },
            item({ item, createElement }) {
              const url = `/catalog/search/?${
                window.cece.searchSection
                  ? `section=${window.cece.searchSection}&`
                  : ''
              }${item.store ? 'stores' : 'brands'}=${encodeURIComponent(
                item.value
              )}`;
              return createElement(
                'div',
                { class: 'catalog-search-item' },
                // Icon
                createElement('span', {
                  class: 'icon',
                  dangerouslySetInnerHTML: {
                    __html: item.store
                      ? `<svg width="20" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 15"><path d="M17 6.2v8.5M3 6.2v8h10v-8" stroke="#000"/><path d="M10.6 6.2v4.7H5.3V6.2M1.7 5.7l1.9-5h12.6l2.1 5H1.7Z" stroke="#000"/></svg>`
                      : `<svg width="18" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 16"><path d="M6.3 1.2 14.1 9l-5.6 5.6L.7 6.8V1.2h5.6Z" stroke="#000"/><path d="M3.8 1.2h5.7L17.3 9l-5.7 5.6L10 13M5 4.4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" stroke="#000"/></svg>`,
                  },
                }),
                // Content
                createElement(
                  'a',
                  {
                    class: 'content',
                    href: url,
                    onClick(e) {
                      e.preventDefault();
                      tracking.trackEvent(
                        'CatalogSearch',
                        'ManualSearch',
                        `${item.store ? 'store' : 'brand'}:${item.value}`
                      );
                      setTimeout(function () {
                        window.location.href = url;
                      }, 50);
                    },
                  },
                  item.value
                  // components.Highlight({
                  //   hit: item,
                  //   attribute: 'value',
                  //   tagName: 'mark',
                  // })
                )
              );
            },
          },
        },
      ]);
    },
  };
}
